import React, { ReactElement, useEffect, useImperativeHandle, useState, forwardRef } from 'react';
import { Icon, Stepper } from 'gd-design-library';
import styles from './CheckoutStepper.module.scss';
import classnames from 'classnames/bind';
import { Button } from '@components';
import { HEAD_TITLES } from '@constants';
import Head from 'next/head';
import { Cart } from '@services/cart';
import { OrderExpanded } from '@services/checkout';
import { useRouter } from 'next/router';
import axios from 'axios';
import { API_HOST_PROXY } from '@constants/api';

const cx = classnames.bind(styles);

// Helper function to clone element with additional props
const cloneElementWithProps = (
  element: React.ReactElement,
  props: Record<string, unknown>
): React.ReactElement =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  React.cloneElement(element, props as any);

const CN = 'stepper';
const mainContentClassName = `${CN}__main-content`;
const sideContentClassName = `${CN}__side-content`;
const contentContainerClassName = `${CN}__content-container`;
const stepperContentClassName = `${CN}__content`;
const buttonClassName = `${CN}__button`;
const previousStepButtonClassName = `${buttonClassName}--previous-step`;
const buttonsClassName = `${CN}__buttons`;
const nextStepPayButtonClassName = `${buttonClassName}--next-step-pay`;


interface StepperStep {
  stepperLabel: string,
  stepperContent: ReactElement | null,
  previousStepText?: string,
  nextStepText?: string,
  onNextStep?: () => Promise<boolean>,
  nextStepButton?: ReactElement<{ onNextStep?: () => void }>,
  isValid?: boolean,
}

interface StepperProps {
  steps: StepperStep[];
  sideContent: ReactElement | null,
  className?: string;
  cart: Cart | OrderExpanded | null;
}

export interface CheckoutStepperHandle {
  next: () => void;
  goTo: (index: number) => void;
}

export const CheckoutStepper = forwardRef<CheckoutStepperHandle, StepperProps>(({
  steps,
  className,
  sideContent,
  cart
}, ref) => {
  const [ activeStepIndex, setActiveStepIndex ] = useState(1);
  const [ isNextStepButtonLoading, setIsNextStepButtonLoading ] = useState(false);
  const router = useRouter();

  const lastStepIndex = steps.length - 1;

  useImperativeHandle(ref, () => ({
    next: (): void => setActiveStepIndex(prev => Math.min(prev + 1, lastStepIndex)),
    goTo: (index): void => setActiveStepIndex(Math.max(0, Math.min(index, lastStepIndex)))
  }), [ lastStepIndex ]);

  const activeStep = steps[activeStepIndex];
  const isCustomerInfoStep = activeStepIndex === 1;

  useEffect(() => {
    const currentUrlSearchParams = new URLSearchParams(window.location.href);
    const isStripeRedirect = currentUrlSearchParams.get('is_stripe_redirect') === 'true';
    const paymentIntentId = currentUrlSearchParams.get('payment_intent');

    const fetchPaymentStatusAndRedirect = async (paymentIntentId: string): Promise<void> => {
      const { data: { status: paymentStatus } } = await axios.get(`${API_HOST_PROXY}/stripe/payments/${paymentIntentId}/status`);

      if (paymentStatus === 'succeeded' || paymentStatus === 'requires_capture') {
        setActiveStepIndex(4);
      } else {
        setActiveStepIndex(3);
      }
    };

    if (isStripeRedirect) {
      fetchPaymentStatusAndRedirect(paymentIntentId);
    }
  }, []);

  const handleNext = async (): Promise<void> => {
    setIsNextStepButtonLoading(true);
    if (activeStep.isValid && (await activeStep.onNextStep?.())) {
      setActiveStepIndex(prevActiveStep => prevActiveStep + 1);
    }
    setIsNextStepButtonLoading(false);
  };

  const handleBack = (): void => {
    if (activeStepIndex > 0) {
      setActiveStepIndex(prevActiveStep => prevActiveStep - 1);
    }
  };

  const handleBackToCart = (): void => {
    router.push({ pathname: '/', query: { 'is-cart-drawer-open': 'true' } });
  };

  const renderButtons = (): React.ReactElement => (
    <div className={ cx(buttonsClassName) }>
      {isCustomerInfoStep && (
        <Button
          iconStart={ <Icon name='arrowLeft' width={ 12 } height={ 12 } /> }
          variant='outlined'
          className={ cx(previousStepButtonClassName) }
          onClick={ handleBackToCart }
        >
          Back
        </Button>
      )}
      {activeStep.previousStepText && (
        <Button
          iconStart={ <Icon name='arrowLeft' width={ 12 } height={ 12 } /> }
          variant='outlined'
          className={ cx(previousStepButtonClassName) }
          onClick={ handleBack }
        >
          {activeStep.previousStepText}
        </Button>
      )}
      {activeStep.nextStepButton
        ? cloneElementWithProps(activeStep.nextStepButton, {
          onNextStep: () => setActiveStepIndex(prev => prev + 1)
        } as Record<string, unknown>)
        : activeStep.nextStepText && (
          <Button
            loading={ isNextStepButtonLoading }
            iconEnd={ <Icon name='arrowRight' width={ 12 } height={ 12 } /> }
            disabled={ !activeStep.isValid || !cart?.lineItems.length }
            onClick={ handleNext }
            className={ activeStep.nextStepText === 'Pay' ? cx(nextStepPayButtonClassName) : '' }
          >
            {activeStep.nextStepText}
          </Button>
        )}
    </div>
  );

  const renderContent = (): React.ReactElement => (
    <div className={ cx(contentContainerClassName) }>
      {cart?.lineItems.length ? (
        <div className={ cx(mainContentClassName) }>
          {process.env.NEXT_PUBLIC_PAYMENT_PROVIDER === 'stripe' && activeStep.stepperLabel === 'Payment'
            ?
            <div className={ cx(stepperContentClassName) }>
              { cloneElementWithProps(activeStep.stepperContent, {
                previousStepText: activeStep.previousStepText,
                nextStepText: activeStep.nextStepText,
                handleBack,
                handleNext,
                cart
              } as Record<string, unknown>) }
            </div>
            : <>
              <div className={ cx(stepperContentClassName) }>
                {activeStep.stepperContent}
              </div>
              {(activeStep.previousStepText || activeStep.nextStepText) && renderButtons()}
            </>}
        </div>
      ) : null}
      {sideContent && <div className={ cx(sideContentClassName) }>{sideContent}</div>}
    </div>
  );

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [ activeStepIndex ]);

  return (
    <>
      <Head>
        <title>{`${activeStep.stepperLabel} ${HEAD_TITLES.CheckoutStep}`}</title>
      </Head>
      <div className={ className }>
        <Stepper
          className={ cx(CN) }
          activeStep={ activeStepIndex }
          isIconsView
          steps={ steps.map(({ stepperLabel }, index) => ({
            label: stepperLabel,
            customView: index >= activeStepIndex ? index + 1 : undefined
          })) }
        />
        {renderContent()}
      </div>
    </>
  );
});

CheckoutStepper.displayName = 'CheckoutStepper';
